import { Box, FormControl, FormErrorMessage, Flex } from '@chakra-ui/react';
import { Field } from 'formik';
import { FormCustomLabel } from './FormCustomLabel';
import React, { useEffect } from 'react';
import { isInvalidField } from '@/lib/util';
import { fieldContainerStyles } from '../styles';
import { renderErrors } from '@/ui/util';
import { formFieldComponents } from '../formFields';
import { RatingReadonlyPill } from '../formFields/RatingReadonlyPill';
import { checkCondition } from '../helpers';
import { FormNotes } from './FormNotes';
import { KText } from '@/ui-library/components';

export interface FormFieldContainerProps {
  item?: Record<string, any>;
  formik?: Record<string, any>;
  useGridStyles?: boolean;
}

export const FormFieldContainer = ({
  item = {},
  formik = {},
  useGridStyles = false,
}: FormFieldContainerProps = {}) => {
  const {
    type,
    name,
    isRequired,
    containerStyles = {},
    isConditional = false,
    showNotes,
    noteCondition,
    noteValues,
    showMandatoryIcon,
    showAfterContent = false,
    AfterContentsComp = <></>,
    showAfterFormContainerContent = false,
    AfterFormContainerComp = <></>,
    formControlContainerStyles = { flexDir: 'column' },
    afterFormContainerCompProps,
    formFieldContainerStyles = {},
    isNewFilter,
    fieldValidate = () => {},
    showCustomNote = false,
    showErrorInUi = true,
    customNoteValue,
    formControlStyles = {},
    formErrorMessageStyles = {},
  } = item;
  const { setFieldValue = () => {}, values = {} } = formik;
  const notesIndex = checkCondition(formik?.values, noteCondition);
  const currentNote = noteValues?.[notesIndex];

  // isRequired may be a function of the form values (e.g. fields that are
  // required only once their optional section has data)
  const resolvedIsRequired =
    typeof isRequired === 'function' ? !!isRequired(values) : isRequired;
  // subLabel may be one too, for a note that answers the value rather than the
  // field — "this field is needed to compute X" has to go once X can compute.
  const resolvedSubLabel =
    typeof item.subLabel === 'function' ? item.subLabel(values) : item.subLabel;
  const resolvedItem =
    typeof isRequired === 'function' || typeof item.subLabel === 'function'
      ? { ...item, isRequired: resolvedIsRequired, subLabel: resolvedSubLabel }
      : item;

  const value = values[name];
  // delete the value if the field is not mounted
  useEffect(() => {
    setFieldValue(name, value);
    return () => {
      if (isConditional) setFieldValue(name, formik?.initialValues?.[name]);
      else setFieldValue(name, undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!item?.name) return null;

  // Rating field: when not editable and has a value, show readonly pill (same as InterviewScorecardReadonly)
  if (
    type === 'radio2' &&
    name === 'rating' &&
    item.editable === false &&
    value
  ) {
    return (
      <Box
        key={name}
        {...(isNewFilter || useGridStyles ? {} : fieldContainerStyles)}
        {...(isNewFilter ? {} : containerStyles)}
      >
        <FormControl
          isRequired={resolvedIsRequired || showMandatoryIcon}
          {...formControlStyles}
        >
          <Flex {...(isNewFilter ? {} : formControlContainerStyles)}>
            <FormCustomLabel item={resolvedItem} />
            <Box flex="1" {...formFieldContainerStyles}>
              <RatingReadonlyPill value={value} options={item.options} />
            </Box>
          </Flex>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box
      key={name}
      // Scroll anchor for a blocked submit. An input can be found by `name`,
      // but a select renders none — so nothing could scroll to a required
      // dropdown the user had missed (ENG-3988).
      data-field-name={name}
      {...(isNewFilter || useGridStyles ? {} : fieldContainerStyles)}
      {...(isNewFilter ? {} : containerStyles)}
    >
      <Field
        name={name}
        validate={() => fieldValidate({ value, formik })}
        //todo: need to check this
        // validate={(value) => validateFormField(value, item)}
      >
        {({ field, form, meta }) => (
          <FormControl
            isInvalid={isInvalidField(form, meta)}
            isRequired={resolvedIsRequired || showMandatoryIcon}
            {...formControlStyles}
          >
            <Flex {...(isNewFilter ? {} : formControlContainerStyles)}>
              <FormCustomLabel item={resolvedItem} />
              <Box flex="1" {...formFieldContainerStyles}>
                {type in formFieldComponents &&
                  React.createElement(formFieldComponents[type], {
                    item: resolvedItem,
                    field,
                    formik,
                  })}
              </Box>
              {showAfterFormContainerContent && (
                <AfterFormContainerComp
                  formik={formik}
                  {...afterFormContainerCompProps}
                />
              )}
            </Flex>
            {showErrorInUi && (
              <FormErrorMessage
                flexDir="column"
                alignItems="flex-start"
                {...formErrorMessageStyles}
              >
                <Box>{renderErrors(field?.label, meta?.error)}</Box>
              </FormErrorMessage>
            )}
            {!showCustomNote && showNotes && currentNote?.length > 0 && (
              <FormNotes noteValue={currentNote} />
            )}
            {showCustomNote && (
              <Flex
                alignItems="center"
                p="15px"
                background="ats-bg-primary"
                mt="4px"
                borderRadius="6px"
              >
                <KText fontSize="sm" ml="4px">
                  <b>Note:</b> {customNoteValue}
                </KText>
              </Flex>
            )}
            {showAfterContent && AfterContentsComp && (
              <AfterContentsComp value={formik?.values?.[name]} />
            )}
          </FormControl>
        )}
      </Field>
    </Box>
  );
};
